import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { VisualActivityProvider } from "@-/dev.improve.haniverse.lib.visual-activity";
import { LearnFlashcardShell } from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import { GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import {
  isSlideLearnCardPresentationKey,
  learnCardPresentationKey,
} from "@-/dev.improve.haniverse.lib.learn.card-action";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { View, type LayoutChangeEvent, useWindowDimensions } from "@-/com.npmjs.react-native";
import Animated, { useAnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import { isSentenceProductionDrillKind } from "@-/dev.improve.haniverse.lib.drill.entities";
import { KeyboardAvoidingView } from "@-/com.npmjs.react-native-keyboard-controller";
import { useNavToolbarPublisher } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { LearnFlashcardActionArea } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck._components.action-area";
import { LearnDeckHeaderControls } from "@-/dev.improve.haniverse.routes.nav.app.learn._components.header";
import {
  LearnDeckContext,
  LearnDeckHistoryContext,
  LearnDeckPresenceContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useCardScrollOptInState } from "@-/dev.improve.haniverse.lib.cards.review-swipe";
import {
  LearnMapCardsBackdrop,
  useLearnMapCardSlideStyle,
  useLearnMapModeSelector,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { useCallback, useEffect, useMemo } from "react";
import {
  LearnFlashcardHorizontalGestureBlockersContext,
  pagedSuccessorOffset,
  useLearnCardHorizontalGesture,
  useLearnFlashcardHorizontalGestureBlockers,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";

import { useLearnDeckController } from "#use-learn-deck-controller";
import { useLearnSurfacePreloads } from "#use-learn-surface-preloads";
import { useHiddenBodyRetention } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { LearnDeckFlashcardSlot } from "#learn-deck-card-slot";
import { useLearnFlashcardStageTopInsets } from "#use-learn-card-stage-top-insets";

const INACTIVE_CARD_SCROLL = { enableScroll: () => {}, isCard: true, isScrollEnabled: false };

/**
 * The persistent Learn host: the card stage (current card over its prepared successor) and the
 * stationary action area below it (on the right in landscape). Reveals and ratings create lightweight router history
 * checkpoints while these prepared card surfaces remain mounted.
 *
 * Layout: the stage flexes into the space left after the action area, so a card never covers the
 * buttons or reaches the tabs. When the keyboard opens the whole deck is padded above it, so the
 * action row stays reachable and the card shrinks and scrolls.
 *
 * The deck lends its undo/redo controls to the Learn toolbar. System Back and undo restore the
 * same history checkpoint, including reversing the saved rating when returning to an answer.
 */
export function LearnFlashcardDeck({
  isFocused,
  ownsToolbar = isFocused,
  previewCardHeight = null,
  ...controllerOptions
}: Parameters<typeof useLearnDeckController>[0] & {
  isFocused: boolean;
  /**
   * The deck is Learn's current route. Its toolbar controls stay published while another tab is
   * in front: they live in the hidden Learn scene, and republishing on every return remounted the
   * whole control row (glass buttons, menus) in the same task as the tab switch.
   */
  ownsToolbar?: boolean;
  /** Under the Learn summary: pin the card to the summary card's height so it underlays it exactly. */
  previewCardHeight?: number | null;
}) {
  // The host owns focus, including its summary/exploration modes. A second navigation-store
  // subscription here schedules synchronous deck work even when its result is overridden.
  const { cardTop } = useLearnFlashcardStageTopInsets();
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const {
    contextValue,
    horizontalDrag,
    isRestoringCard,
    onStageLayout,
    onSurfaceReady,
    onSurfacesReleased,
    ...deck
  } = useLearnDeckController({ ...controllerOptions, isFocused });
  const { closeExploration, exploration, stage, state } = deck;
  const isExploring = exploration !== null;
  // A slide card's successor waits just past the window's right edge: the card bounds start 9pt
  // (plus the landscape margin) in from its left.
  const { width: windowWidth } = useWindowDimensions();
  const pageStride = windowWidth - 9 - (isLandscape ? diameter + 9 : 0);
  useEffect(() => {
    stage.pageStride.set(pageStride);
  }, [pageStride, stage]);
  // The placeholder stands in for the successor, so it pages in with it beside a slide card. While a
  // slide card pages out, the card paging in owns the motion and the placeholder stays under it.
  const successorPlaceholderStyle = useAnimatedStyle(() => {
    const offset = pagedSuccessorOffset(stage, stage.motionPresentation.get().current);
    const departure = stage.departure.get();
    const isPagingIn =
      departure !== null &&
      isSlideLearnCardPresentationKey(departure.presentation) &&
      stage.departureProgress.get() < 1;
    return {
      transform: [
        {
          translateX:
            offset !== null ? stage.dragX.get() + offset : isPagingIn ? stage.dragX.get() : 0,
        },
      ],
    };
  });
  const scrollOptIn = useCardScrollOptInState(
    learnCardPresentationKey(state.current.presentation),
    isExploring ||
      state.face === "back" ||
      state.view === "info" ||
      state.current.item.kind === "slide" ||
      state.current.item.kind === "quiz",
  );
  const [history] = useContextSelectors(LearnDeckHistoryContext, (c) => c);
  // The deck deliberately does NOT claim opaque chrome: a dismissed card travels up and out under
  // the toolbar, and the shared header fade dissolves it inside the toolbar's 9px gutter.

  const canRevealOnHorizontalDrag =
    (state.current.item.kind === "lesson" ||
      (state.current.item.kind === "review" &&
        !isSentenceProductionDrillKind(state.current.item.drillType.kind))) &&
    state.face === "front" &&
    state.view === "study";
  const horizontalGestureBlockers = useLearnFlashcardHorizontalGestureBlockers();
  const { cardControls } = contextValue;
  const slidePages = cardControls?.kind === "slide" ? cardControls.pages : null;
  const horizontalGesture = useLearnCardHorizontalGesture({
    allowVerticalScroll: scrollOptIn.value.isScrollEnabled,
    blockerViews: horizontalGestureBlockers.views,
    // Touches are taken whenever the deck is in front. Whether a card may move, and what its
    // release does, is decided per touch on the UI thread, and a commit the card cannot take yet
    // (controls still registering, a flip finishing, its promotion pending) is held, not dropped:
    // enabling the recognizer per React state left every touch that began before React caught up
    // dead for its whole lifetime.
    isEnabled: isFocused && !controllerOptions.isHistoryPending && !isRestoringCard,
    onCommit: horizontalDrag.onCommit,
    onDragCancel: horizontalDrag.onDragCancel,
    onDragStart: horizontalDrag.onDragStart,
    onPark: horizontalDrag.onPark,
    // Exploring, the card stays put: a swipe presses the in-card Back to Review instead.
    onSwipeBack: isExploring ? closeExploration : undefined,
    // Until a slide's last view, a swipe moves on to its next view instead of dismissing it.
    pages: slidePages,
    presentationKey: learnCardPresentationKey(state.current.presentation),
    revealOnDragStart: canRevealOnHorizontalDrag && state.current.item.kind === "review",
    stage,
  });

  // Where the card rests, for the chrome it slides over when Learn's map opens. The stage starts
  // at the top of Learn's cards layer, the coordinates that frame is in.
  const mapCardFrame = useLearnMapModeSelector((mode) => mode.cardFrame);
  const mapCardSlideStyle = useLearnMapCardSlideStyle();
  const handleCardBoundsLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height, width, y } = event.nativeEvent.layout;
      onStageLayout({ height, width });
      mapCardFrame?.set({ bottom: y + height, top: y });
    },
    [mapCardFrame, onStageLayout],
  );

  const isLearnFocused = controllerOptions.isLearnFocused ?? isFocused;
  // Learn stays mounted behind other tabs with its three off-Learn surfaces for as long as it is away,
  // so returning, revealing and advancing find them prepared. Only a memory warning trims the deck
  // to the visible face.
  const isRetainedWhileBlurred = useHiddenBodyRetention(isLearnFocused, {
    delayMs: null,
    retainInitially: true,
  });
  const hasSurface = useLearnSurfacePreloads(state, isLearnFocused, !isRetainedWhileBlurred);
  // Back to front: the flashcard queue from its far end to the card under the current one, the
  // current card, then the card a rating promoted past, flying off above it, and the one before it,
  // landed and hidden, kept for Undo. A queued slot renders once its front surface is admitted; the
  // cards promoted past always do (they are already mounted, on screen or flying out of the way).
  const { departing, previous } = state;
  const renderedSlots = [
    ...state.queue.flatMap((_entry, index) => {
      const farther = state.queue[state.queue.length - 1 - index];
      return farther?.kind === "card" ? [farther.slot] : [];
    }),
    state.current,
    departing?.slot ?? null,
    previous?.slot ?? null,
  ].filter(
    (slot): slot is NonNullable<typeof slot> =>
      slot != null &&
      (slot === departing?.slot || slot === previous?.slot || hasSurface(slot, "front")),
  );

  // Readiness and lookahead are private to the deck. Publishing the entire deck context here
  // also rebuilt the toolbar on every hidden-surface commit.
  const publishToolbar = useNavToolbarPublisher();
  const toolbarActions = cardControls?.kind === "slide" ? cardControls.actions : null;
  const { isTransitioning, savedActionUndo } = contextValue;
  // On a slide with views, Undo and Redo step through its views; past either end they step the
  // deck's history as usual. Undo is still pending while the card it brought back waits to slide in.
  const toolbarHistory = useMemo(() => {
    const restoring = isRestoringCard
      ? { ...history, isPending: true, pendingStep: "back" as const }
      : history;
    if (slidePages === null) {
      return restoring;
    }
    const { count, current, goTo } = slidePages;
    const hasPreviousView = current > 0;
    const hasNextView = current < count - 1;
    return {
      ...restoring,
      canGoBack: hasPreviousView || history.canGoBack,
      canGoForward: hasNextView || history.canGoForward,
      goBack: hasPreviousView ? () => goTo(current - 1) : history.goBack,
      goForward: hasNextView ? () => goTo(current + 1) : history.goForward,
    };
  }, [history, isRestoringCard, slidePages]);
  useEffect(() => {
    if (publishToolbar === null || !ownsToolbar) {
      return;
    }
    return publishToolbar({
      actions: toolbarActions,
      leading: null,
      panels: (
        <LearnDeckPresenceContext.Provider value>
          <LearnDeckHistoryContext.Provider value={toolbarHistory}>
            <LearnDeckHeaderControls
              isTransitioning={isTransitioning}
              savedActionUndo={savedActionUndo}
            />
          </LearnDeckHistoryContext.Provider>
        </LearnDeckPresenceContext.Provider>
      ),
    });
  }, [
    isTransitioning,
    ownsToolbar,
    publishToolbar,
    savedActionUndo,
    toolbarActions,
    toolbarHistory,
  ]);

  return (
    <VisualActivityProvider active={isFocused}>
      <LearnDeckPresenceContext.Provider value>
        <LearnDeckContext.Provider value={contextValue}>
          {/* Measure screen-relative overlap: the navigator already reserves the bottom tabs,
            so adding the entire keyboard height would leave a tab-sized gap above it. */}
          <KeyboardAvoidingView
            automaticOffset
            behavior="padding"
            className="flex-1"
            enabled={isFocused}
          >
            <View className="min-h-0 flex-1">
              <LearnMapCardsBackdrop />
              {/* NOT clipped: a dismissed card travels up out of these bounds, under the toolbar,
              dissolving into the shared header fade. `overflow-hidden` would scissor it at the
              stage's top edge instead, and the card would simply vanish. */}
              <View
                className="min-h-0 flex-1"
                pointerEvents="box-none"
                style={
                  isLandscape ? { marginLeft: diameter + 9, marginRight: diameter + 9 } : undefined
                }
                testID="learn-card-stage"
              >
                {/* The deck owns both the gesture and its detector. Moving the same mutable RNGH
                gesture between card detectors lets outgoing cleanup detach the live handler.
                In-card controls (the pinyin keyboard) register views it can't start from.
                The bounds take touches themselves (`auto`, not `box-none`): a card whose
                surfaces are not interactive right now (its action is in flight, or it was
                uncovered by a release ahead of its promotion) must still be grabbed, and with
                nothing inside hit-testable the pan never saw the touch at all. */}
                <LearnFlashcardHorizontalGestureBlockersContext.Provider
                  value={horizontalGestureBlockers.register}
                >
                  {/* The card alone leaves for Learn's map, up over the toolbar; the stage and
                  the controls below it stay where they are. */}
                  <Animated.View
                    className="absolute inset-0"
                    pointerEvents="box-none"
                    style={mapCardSlideStyle}
                  >
                    <GestureDetector
                      gesture={horizontalGesture}
                      touchAction={scrollOptIn.value.isScrollEnabled ? "pan-y" : "none"}
                    >
                      <View
                        className={`absolute inset-x-2.25 ${previewCardHeight === null ? "bottom-2.25" : ""}`}
                        collapsable={false}
                        onLayout={handleCardBoundsLayout}
                        {...scrollOptIn.touchHandlers}
                        pointerEvents="auto"
                        style={
                          previewCardHeight === null
                            ? { top: cardTop }
                            : { height: previewCardHeight, top: cardTop }
                        }
                        testID="learn-card-bounds"
                      >
                        {/* A cheap surface remains under every card, including while the next
                      presentation is still resolving or waiting for its preload budget. */}
                        <Animated.View
                          accessibilityElementsHidden
                          className="absolute inset-0"
                          importantForAccessibility="no-hide-descendants"
                          pointerEvents="none"
                          style={successorPlaceholderStyle}
                        >
                          <LearnFlashcardShell
                            className="absolute inset-0"
                            testID="learn-deck-successor-placeholder"
                          >
                            {null}
                          </LearnFlashcardShell>
                        </Animated.View>
                        {renderedSlots.map((slot) => {
                          const isCurrent = slot === state.current;
                          // Drawn as it was rated, never interactive; it keeps its mounted back.
                          const departure =
                            slot === departing?.slot
                              ? departing
                              : slot === previous?.slot
                                ? previous
                                : null;
                          const props = {
                            face: isCurrent ? state.face : (departure?.face ?? ("front" as const)),
                            isActive: isCurrent && isFocused,
                            isInteractionPending: deck.isTransitioning,
                            onSelectView: deck.switchView,
                            onSurfaceReady,
                            preloadBack: hasSurface(slot, "back") || departure?.face === "back",
                            role: isCurrent
                              ? ("current" as const)
                              : departure === null
                                ? ("successor" as const)
                                : departure === previous
                                  ? ("previous" as const)
                                  : ("departing" as const),
                            stage,
                            view: isCurrent ? state.view : (departure?.view ?? ("study" as const)),
                          };
                          // Each card carries its OWN presentation, so its controls claim and release
                          // the action area under an identity that survives promotion — a card being
                          // dismissed can never release the claim of the card replacing it.
                          //
                          // Recovery belongs to the presentation, never its current/successor role.
                          // Promotion must retain the prepared surfaces and their running animations.
                          return (
                            <LearnDeckFlashcardSlot
                              {...props}
                              isCurrentPresentation={isCurrent}
                              isDeckFocused={isCurrent && isFocused}
                              item={slot.item}
                              key={deck.getCardRenderKey(
                                learnCardPresentationKey(slot.presentation),
                              )}
                              onSurfacesReleased={onSurfacesReleased}
                              presentation={slot.presentation}
                              scrollOptIn={isCurrent ? scrollOptIn.value : INACTIVE_CARD_SCROLL}
                            />
                          );
                        })}
                      </View>
                    </GestureDetector>
                  </Animated.View>
                </LearnFlashcardHorizontalGestureBlockersContext.Provider>
              </View>
              {/* Always mounted, including under the summary: it hosts the answer-input portal. Without
              the host, native teleport renders the input inline at the top of the card. The
              summary's own controls cover this row there, so it hides beneath them. */}
              <LearnFlashcardActionArea
                isActive={isFocused}
                isCovered={previewCardHeight !== null}
              />
            </View>
          </KeyboardAvoidingView>
        </LearnDeckContext.Provider>
      </LearnDeckPresenceContext.Provider>
    </VisualActivityProvider>
  );
}
